// Exportación de la ficha de un paciente como PDF (su historia clínica).
//
// Genera un documento autocontenido (HTML + estilos inline) y lo abre para imprimir/guardar
// como PDF vía un Blob (imprimirDocumentoHTML): evita el `document.write` que Chrome rompe
// (dejaba la ventana en blanco en la PC) y mantiene la ventana que ya funciona en el iPhone.
import { imprimirDocumentoHTML } from "./impresion";

// Escapa texto del usuario para que no rompa (ni inyecte) el HTML del documento.
function esc(valor) {
  return String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function formatearFechaISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtMoneda(n) {
  return `$${(Number(n) || 0).toLocaleString("es-UY")}`;
}

// `paciente`: doc del paciente. `notas`: evoluciones ya mezcladas y ordenadas (legacy +
// subcolección). `usuario`: perfil para el encabezado.
export function exportarFichaPacientePDF(paciente, notas = [], usuario = {}) {
  if (!paciente) return;

  const tituloClinica =
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ").trim() || "Vedent";

  const antecedentes = [
    ["Alergias", paciente.alergias],
    ["Enfermedades", paciente.enfermedades],
    ["Observaciones", paciente.observaciones],
  ]
    .filter(([, v]) => String(v || "").trim())
    .map(([etiqueta, v]) => `<p><strong>${etiqueta}:</strong> ${esc(v)}</p>`)
    .join("");

  const historiaHtml = (notas || []).length
    ? notas
        .map(
          (n) =>
            `<div class="evo"><h4>Evolución del ${formatearFechaISO(n.fecha)}</h4>` +
            `<div class="evo-txt">${esc(n.texto).replace(/\n/g, "<br>")}</div></div>`
        )
        .join("")
    : `<p class="vacio">Sin evoluciones registradas.</p>`;

  const items = Array.isArray(paciente.presupuesto) ? paciente.presupuesto : [];
  let totalGeneral = 0;
  let pagadoGeneral = 0;
  const filas = items
    .map((it) => {
      const precio = Number(it.precio) || 0;
      const desc = Number(it.descuento) || 0;
      const conDesc = precio * (1 - desc / 100);
      const pagado = Number(it.pagado) || 0;
      totalGeneral += conDesc;
      pagadoGeneral += pagado;
      return (
        `<tr><td>${esc(it.nombre)}</td>` +
        `<td class="num">${fmtMoneda(conDesc)}</td>` +
        `<td class="num">${fmtMoneda(pagado)}</td>` +
        `<td class="num">${fmtMoneda(conDesc - pagado)}</td></tr>`
      );
    })
    .join("");
  const presupuestoHtml = items.length
    ? `<table class="tabla">
         <thead><tr><th>Tratamiento</th><th class="num">Importe</th><th class="num">Pagado</th><th class="num">Saldo</th></tr></thead>
         <tbody>${filas}</tbody>
         <tfoot><tr><th>Total</th><th class="num">${fmtMoneda(totalGeneral)}</th><th class="num">${fmtMoneda(pagadoGeneral)}</th><th class="num">${fmtMoneda(totalGeneral - pagadoGeneral)}</th></tr></tfoot>
       </table>`
    : `<p class="vacio">Sin presupuesto registrado.</p>`;

  const datos = [
    paciente.ci ? `<strong>CI/ID:</strong> ${esc(paciente.ci)}` : "",
    paciente.telefono ? `<strong>Teléfono:</strong> ${esc(paciente.telefono)}` : "",
    paciente.mutualista ? `<strong>Mutualista:</strong> ${esc(paciente.mutualista)}` : "",
  ]
    .filter(Boolean)
    .join(" &nbsp;·&nbsp; ");

  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Ficha - ${esc(paciente.nombre)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, sans-serif; color: #1f2937; margin: 32px; line-height: 1.5; }
  .clinica { font-size: 13px; color: #678AB2; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; }
  h1 { font-size: 22px; margin: 4px 0 0; }
  .datos { color: #4b5563; font-size: 14px; margin: 4px 0 0; }
  .emitido { color: #9ca3af; font-size: 12px; margin-top: 4px; }
  h2 { font-size: 15px; color: #678AB2; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; margin: 28px 0 12px; }
  .evo { margin-bottom: 14px; }
  .evo h4 { margin: 0 0 2px; font-size: 13px; color: #374151; }
  .evo-txt { font-size: 14px; white-space: pre-wrap; }
  .vacio { color: #9ca3af; font-style: italic; font-size: 14px; }
  table.tabla { width: 100%; border-collapse: collapse; font-size: 14px; }
  table.tabla th, table.tabla td { border: 1px solid #e5e7eb; padding: 8px 10px; text-align: left; }
  table.tabla .num { text-align: right; white-space: nowrap; }
  table.tabla thead th, table.tabla tfoot th { background: #f3f4f6; }
  @media print { body { margin: 12mm; } }
</style></head>
<body>
  <div class="clinica">${esc(tituloClinica)}</div>
  <h1>${esc(paciente.nombre)}</h1>
  ${datos ? `<p class="datos">${datos}</p>` : ""}
  <p class="emitido">Emitido el ${formatearFechaISO(new Date().toISOString())}</p>

  ${antecedentes ? `<h2>Antecedentes</h2>${antecedentes}` : ""}

  <h2>Historia clínica</h2>
  ${historiaHtml}

  <h2>Presupuesto y cuenta</h2>
  ${presupuestoHtml}
</body></html>`;

  imprimirDocumentoHTML(html);
}
